import React, { useState } from "react";
import { Select } from "antd";
import { Button, Message, Input, Upload } from "../../../BP2Components";
import { useDispatch } from "react-redux";
import { setWidgetInSheetParams } from "appRedux/actions/Actions";
import IntlMessages from "@util/IntlMessages";
import { SketchPicker } from "react-color";
import { deleteFile, sendFileToCloudStorage } from "services/restAPI/RestApi";
import { useIntl } from "react-intl";
import { getBase64StringFromDataURL } from "../../../../util/Scripts";

const { Option } = Select;

const PercentageSummarySettings = ({ value }) => {
   const dispatch = useDispatch();
   const intl = useIntl();

   const [settings, setSettings] = useState({
      ...value,
   });

   const handleDeletePhoto = () => {
      Message.loading(intl.formatMessage({ id: "global.loading" }), 0);
      if (settings.imageFileId && !settings.imageUrl) {
         deleteFile(settings.imageFileId)
            .then(() => {
               setSettings({
                  ...settings,
                  imageFileId: null,
               });
               Message.success(intl.formatMessage({ id: "global.done" }));
            })
            .catch(() => {
               Message.error(intl.formatMessage({ id: "global.error" }));
            })
            .finally(() => {
               Message.destroy();
            });
      } else {
         setSettings({
            ...settings,
            imageFileId: null,
            imageUrl: null,
         });
         Message.destroy();
      }
   };

   const handleUploadFile = (info) => {
      if (!info.type.startsWith("image/")) {
         Message.error(intl.formatMessage({ id: "forms.wrongFileType" }));
         return;
      }

      const cleanedType = info.type.replace(/^image\//, "");
      const reader = new FileReader();

      reader.onload = function (loadEvent) {
         const result = loadEvent.target.result;
         const base64 = getBase64StringFromDataURL(result);
         if (base64) {
            Message.loading(intl.formatMessage({ id: "global.loading" }));
            try {
               sendFileToCloudStorage(info.name, cleanedType, base64).then((res) => {
                  setSettings({
                     ...settings,
                     imageUrl: res?.data?.cloudStorage?.url,
                  });
               });
               Message.success(`${intl.formatMessage({ id: "forms.uploadSucces" })} ${info.name}`);
            } catch (error) {
               console.log(error);
               Message.error(intl.formatMessage({ id: "forms.uploadFailed" }));
            } finally {
               Message.destroy();
            }
         }
      };
      reader.readAsDataURL(info);
   };

   const handleSubmit = (e) => {
      e.preventDefault();

      dispatch(setWidgetInSheetParams(settings));
      Message.success(intl.formatMessage({ id: "global.saved" }));
   };
   const handleChange = (e) => {
      setSettings({ ...settings, [e.target.name]: e.target.value });
   };
   const handleChangeInput = (name) => (value) => {
      setSettings({ ...settings, [name]: value });
   };

   const handleDefaultColor = (type) => {
      if (type === "textColor") {
         setSettings({ ...settings, textColor: "#000" });
      } else {
         setSettings({ ...settings, backgroundColourHex: "#fff" });
      }
   };

   const [displayColorPicker, setDisplayColorPicker] = useState(false);
   const [displayColorPickerText, setDisplayColorPickerText] = useState(false);

   const togglePicker = (type) => {
      if (type === "textColor") {
         setDisplayColorPickerText(!displayColorPickerText);
      } else {
         setDisplayColorPicker(!displayColorPicker);
      }
   };

   const handleEditListBuilder = () => {
      if (settings.formId) {
         window.open(`/listbuilder?tab=formbuilder&formId=${settings.formId}`, "_blank");
      } else {
         Message.warning(
            intl.formatMessage({ id: "forms.selectForm" }) || "Please select a form first",
         );
      }
   };

   return (
      <>
         <form onSubmit={handleSubmit}>
            <div className="widget-settings">
               <div key="Set" className="widget-settings__inner">
                  <label className="widget-settings__inner__label" key="titleKey">
                     <IntlMessages id="settings.description" />:
                     <Input
                        type="text"
                        name="titleKey"
                        style={{ width: 220 }}
                        onChange={handleChange}
                        value={settings.titleKey}
                     />
                  </label>
                  <div className="widget-settings__inner__label" key="file">
                     <IntlMessages id="settings.upload" />:
                     <div style={{ display: "flex", gap: "10px" }}>
                        <Upload onUpload={handleUploadFile} disabled={settings.imageUrl}>
                           <Button buttonType={settings.imageUrl ? "disabled" : "default"}>
                              <IntlMessages id="settings.upload" />
                           </Button>
                        </Upload>
                        <Button
                           onClick={handleDeletePhoto}
                           buttonType={
                              settings.imageUrl || settings.imageFileId ? "default" : "disabled"
                           }
                        >
                           <IntlMessages id="delete" />
                        </Button>
                     </div>
                     {settings.imageUrl ? (
                        <div className="gx-mt-2">
                           <img src={settings.imageUrl} alt="" style={{ maxWidth: "100px" }} />
                        </div>
                     ) : null}
                  </div>
                  <label className="widget-settings__inner__label" key="xsImageSize">
                     <IntlMessages id="settings.buttonImageWidth" />:
                     <Input
                        type="number"
                        name="xsImageSize"
                        onChange={handleChange}
                        value={settings.xsImageSize}
                     />
                  </label>
                  <label className="widget-settings__inner__label" key="smImageSize">
                     <IntlMessages id="settings.imageHeight" />:
                     <Input
                        type="number"
                        name="smImageSize"
                        onChange={handleChange}
                        value={settings.smImageSize}
                     />
                  </label>
                  <label className="widget-settings__inner__label" key="formId">
                     Form ID:
                     <div style={{ display: "flex", gap: "10px", width: "100%" }}>
                        <Input
                           type="text"
                           name="formId"
                           onChange={handleChange}
                           value={settings.formId}
                        />
                        <Button onClick={handleEditListBuilder} style={{ whiteSpace: "nowrap" }}>
                           <IntlMessages id="global.edit" />
                        </Button>
                     </div>
                  </label>
                  <label className="widget-settings__inner__label" key="dataId">
                     Data ID:
                     <Input
                        type="text"
                        name="dataId"
                        onChange={handleChange}
                        value={settings.dataId}
                     />
                  </label>
                  <label className="widget-settings__inner__label" key="humanFieldName">
                     Human Field Name:
                     <Input
                        type="text"
                        name="humanFieldName"
                        onChange={handleChange}
                        value={settings.humanFieldName}
                     />
                  </label>
                  <label className="widget-settings__inner__label" key="tooltipText">
                     Tooltip text:
                     <Input
                        type="text"
                        name="tooltipText"
                        onChange={handleChange}
                        value={settings.tooltipText}
                     />
                  </label>
                  <label className="widget-settings__inner__label" key="textColor">
                     <IntlMessages id="settings.textColor" />:
                     <div>
                        <Button
                           buttonType="primary"
                           onClick={() => togglePicker("textColor")}
                           className="cp-color"
                           style={{ backgroundColor: settings.textColor }}
                        >
                           <IntlMessages id="settings.clickToChangeColor" />
                        </Button>

                        <Button onClick={() => handleDefaultColor("textColor")}>
                           <IntlMessages id="settings.setDefaultColor" />
                        </Button>
                     </div>
                     {displayColorPickerText && (
                        <div className="cp-popover">
                           <div className="cp-cover" />
                           <SketchPicker
                              color={settings.textColor}
                              onChange={handleChangeColor("textColor")}
                           />
                        </div>
                     )}
                  </label>
               </div>
               <Button buttonType="primary" htmlType="submit">
                  <IntlMessages id="global.preview" />
               </Button>
            </div>
         </form>
      </>
   );
};

export default PercentageSummarySettings;
